import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useParams } from "react-router-dom"
import {
  ArrowLeft,
  Bold,
  Copy,
  Italic,
  List,
  Loader2,
  Mail,
  Pencil,
  Plus,
  Trash2,
  ExternalLink,
} from "lucide-react"
import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { supabase } from "@/lib/supabase"
import { sanitizeHtml } from "@/lib/sanitizeHtml"
import { formatCurrency, formatDate, formatRelativeTime } from "@/lib/format"
import { errorMessage } from "@/lib/errorMessage"
import { usePermission } from "@/hooks/usePermission"
import { RequireOrg } from "@/components/RequireOrg"
import { GenerateFundraiserLinkModal } from "@/components/campaigns/GenerateFundraiserLinkModal"
import { getPublicSiteOrigin } from "@/lib/publicSiteUrl"
import { useAuthStore } from "@/stores/authStore"
import { useOrgStore } from "@/stores/orgStore"
import type { Tables } from "@/types/database"

type Contact = Tables<"contacts">
type Donation = Tables<"donations">
type DonorNote = Tables<"donor_notes">
type DonorTag = Tables<"donor_tags">
type VolunteerActivity = Tables<"volunteer_activities">

type FormSubmissionRow = {
  id: string
  form_data: Record<string, unknown>
  created_at: string
  status: string
  read_at: string | null
  page_section_id: string | null
  page_title: string | null
}

type EventRegistrationRow = {
  id: string
  event_id: string
  status: string
  guest_count: number
  created_at: string
  events: { id: string; title: string; slug: string; start_date: string } | null
}

type FundraiserLinkRow = {
  id: string
  name: string
  share_token: string
  created_at: string
  campaign_id: string
  campaigns: { title: string; slug: string } | null
}

const AUTO_TAGS = ["Donor", "Volunteer"] as const

function formatActivityType(type: string) {
  return type.charAt(0).toUpperCase() + type.slice(1)
}

export function ContactDetailPage() {
  return (
    <RequireOrg>
      <ContactDetailPageContent />
    </RequireOrg>
  )
}

function ContactDetailPageContent() {
  const { contactId } = useParams<{ contactId: string }>()
  const { user } = useAuthStore()
  const { currentOrg, userOrgs } = useOrgStore()
  const { hasPermission: canViewFinancials } = usePermission("can_view_financials")
  const { hasPermission: canManageContacts } = usePermission("can_manage_contacts")
  const { hasPermission: canManageContent } = usePermission("can_manage_content")

  const [contact, setContact] = useState<Contact | null>(null)
  const [donations, setDonations] = useState<Donation[]>([])
  const [activities, setActivities] = useState<VolunteerActivity[]>([])
  const [notes, setNotes] = useState<DonorNote[]>([])
  const [tags, setTags] = useState<DonorTag[]>([])
  const [formSubmissions, setFormSubmissions] = useState<FormSubmissionRow[]>([])
  const [eventRegistrations, setEventRegistrations] = useState<EventRegistrationRow[]>([])
  const [fundraiserLinks, setFundraiserLinks] = useState<FundraiserLinkRow[]>([])
  const [loading, setLoading] = useState(true)
  const [isOrgOwner, setIsOrgOwner] = useState(false)
  const [existingTagsForOrg, setExistingTagsForOrg] = useState<string[]>([])

  const [activeTab, setActiveTab] = useState("donations")

  // Notes state
  const [savingNote, setSavingNote] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [editingNoteText, setEditingNoteText] = useState("")

  const noteEditor = useEditor({
    extensions: [StarterKit],
    content: "",
    editorProps: {
      attributes: {
        class: "min-h-[80px] outline-none prose prose-sm max-w-none",
      },
    },
  })

  // Tags state
  const [tagInput, setTagInput] = useState("")
  const [tagSuggestionsOpen, setTagSuggestionsOpen] = useState(false)
  const [addingTag, setAddingTag] = useState(false)
  const tagInputRef = useRef<HTMLInputElement>(null)
  const tagSuggestionsRef = useRef<HTMLDivElement>(null)

  // Volunteer activity dialog
  const [activityDialogOpen, setActivityDialogOpen] = useState(false)
  const [activityForm, setActivityForm] = useState({
    activity_type: "event" as "event" | "shift" | "project",
    activity_date: new Date().toISOString().slice(0, 10),
    description: "",
    hours: "",
  })
  const [savingActivity, setSavingActivity] = useState(false)
  const [editingActivityId, setEditingActivityId] = useState<string | null>(null)

  const [fundraiserLinkModalOpen, setFundraiserLinkModalOpen] = useState(false)
  const [fundraiserLinksViewOpen, setFundraiserLinksViewOpen] = useState(false)

  // Offline donation dialog
  const [offlineDonationOpen, setOfflineDonationOpen] = useState(false)
  const [offlineForm, setOfflineForm] = useState({
    amount: "",
    date: new Date().toISOString().slice(0, 10),
    method: "cash" as "cash" | "check",
    checkNumber: "",
  })
  const [savingOffline, setSavingOffline] = useState(false)

  // Confirm dialogs
  const [deleteNoteDialog, setDeleteNoteDialog] = useState<{ id: string } | null>(null)
  const [deleteActivityDialog, setDeleteActivityDialog] = useState<{ id: string } | null>(null)

  const orgId = (currentOrg ?? userOrgs[0])?.id

  const fetchIsOrgOwner = useCallback(async () => {
    if (!orgId || !user?.id) return
    const { data } = await supabase
      .from("organization_members")
      .select("role")
      .eq("org_id", orgId)
      .eq("user_id", user.id)
      .single()
    setIsOrgOwner((data?.role ?? "") === "owner")
  }, [orgId, user?.id])

  const fetchContactData = useCallback(async () => {
    if (!contactId || !orgId) return
    setLoading(true)
    try {
      const contactRes = await supabase
        .from("contacts")
        .select("*")
        .eq("id", contactId)
        .eq("org_id", orgId)
        .single()

      if (contactRes.error || !contactRes.data) {
        setContact(null)
        setNotes([])
        setTags([])
        setDonations([])
        setActivities([])
        setFormSubmissions([])
        setEventRegistrations([])
        setFundraiserLinks([])
        setLoading(false)
        return
      }

      const c = contactRes.data as Contact
      setContact(c)

      const [donationsRes, activitiesRes, notesRes, tagsRes, existingTagsRes, submissionsRes, eventRegsRes, fundraisersRes] =
        await Promise.all([
          supabase
            .from("donations")
            .select("*")
            .eq("contact_id", contactId)
            .eq("org_id", orgId)
            .order("created_at", { ascending: false }),
          supabase
            .from("volunteer_activities")
            .select("*")
            .eq("contact_id", contactId)
            .eq("org_id", orgId)
            .order("activity_date", { ascending: false }),
          supabase
            .from("donor_notes")
            .select("*")
            .eq("contact_id", contactId)
            .eq("org_id", orgId)
            .order("created_at", { ascending: false }),
          supabase
            .from("donor_tags")
            .select("*")
            .eq("contact_id", contactId)
            .eq("org_id", orgId),
          supabase
            .from("donor_tags")
            .select("tag")
            .eq("org_id", orgId)
            .neq("tag", "Donor")
            .neq("tag", "Volunteer"),
          supabase
            .from("form_submissions")
            .select("id, form_data, created_at, status, read_at, page_section_id")
            .eq("contact_id", contactId)
            .eq("org_id", orgId)
            .order("created_at", { ascending: false }),
          supabase
            .from("event_attendees")
            .select("id, event_id, status, guest_count, created_at, events (id, title, slug, start_date)")
            .eq("contact_id", contactId)
            .eq("status", "registered")
            .order("created_at", { ascending: false }),
          supabase
            .from("campaign_fundraisers")
            .select("id, name, share_token, created_at, campaign_id, campaigns (title, slug)")
            .eq("contact_id", contactId)
            .eq("org_id", orgId)
            .order("created_at", { ascending: false }),
        ])

      setDonations((donationsRes.data ?? []) as Donation[])
      setActivities((activitiesRes.data ?? []) as VolunteerActivity[])
      setNotes((notesRes.data ?? []) as DonorNote[])
      setTags((tagsRes.data ?? []) as DonorTag[])
      const uniqueExisting = [
        ...new Set(((existingTagsRes.data ?? []) as { tag: string }[]).map((t) => t.tag)),
      ]
      setExistingTagsForOrg(uniqueExisting)

      // Enrich form submissions with page titles
      const rawSubmissions = (submissionsRes.data ?? []) as Array<{
        id: string
        form_data: Record<string, unknown>
        created_at: string
        status: string
        read_at: string | null
        page_section_id: string | null
      }>
      const pageSectionIds = [
        ...new Set(rawSubmissions.map((r) => r.page_section_id).filter((id): id is string => !!id)),
      ]
      const pageTitleMap = new Map<string, string>()
      if (pageSectionIds.length > 0) {
        const { data: sectionsData } = await supabase
          .from("page_sections")
          .select("id, page_id")
          .in("id", pageSectionIds)
        const pageIds = [
          ...new Set((sectionsData ?? []).map((s: { id: string; page_id: string }) => s.page_id)),
        ]
        if (pageIds.length > 0) {
          const { data: pagesData } = await supabase
            .from("pages")
            .select("id, title")
            .in("id", pageIds)
          const pageTitleById = new Map(
            (pagesData ?? []).map((p: { id: string; title: string | null }) => [p.id, p.title ?? "Form"])
          )
          ;(sectionsData ?? []).forEach((s: { id: string; page_id: string }) => {
            pageTitleMap.set(s.id, pageTitleById.get(s.page_id) ?? "Form")
          })
        }
      }
      setFormSubmissions(
        rawSubmissions.map((r) => ({
          ...r,
          page_title: r.page_section_id ? (pageTitleMap.get(r.page_section_id) ?? null) : null,
        }))
      )
      setEventRegistrations((eventRegsRes.data ?? []) as EventRegistrationRow[])
      setFundraiserLinks((fundraisersRes.data ?? []) as FundraiserLinkRow[])
    } catch (err) {
      console.error("Contact fetch error:", err)
      toast.error("Failed to load contact data")
    } finally {
      setLoading(false)
    }
  }, [contactId, orgId])

  useEffect(() => {
    fetchContactData()
  }, [fetchContactData])

  useEffect(() => {
    fetchIsOrgOwner()
  }, [fetchIsOrgOwner])

  const totalDonated = useMemo(
    () =>
      donations
        ?.filter((d) => d.status === "succeeded")
        .reduce((sum, d) => sum + d.amount, 0) ?? 0,
    [donations]
  )

  const volunteerHours = useMemo(
    () => activities?.reduce((sum, a) => sum + (a.hours ?? 0), 0) ?? 0,
    [activities]
  )

  const lastActivityDate = useMemo(() => {
    const lastDonation = donations?.[0]?.created_at
    const lastActivity = activities?.[0]?.activity_date
    if (!lastDonation && !lastActivity) return null
    if (!lastDonation) return lastActivity
    if (!lastActivity) return lastDonation
    return new Date(lastDonation).getTime() > new Date(lastActivity).getTime()
      ? lastDonation
      : lastActivity
  }, [donations, activities])

  const contactTagSet = useMemo(() => new Set(tags.map((t) => t.tag.toLowerCase())), [tags])

  const tagSuggestions = useMemo(() => {
    const input = tagInput.trim().toLowerCase()
    const excludeOwn = (t: string) => !contactTagSet.has(t.toLowerCase())
    if (!input) return existingTagsForOrg.filter(excludeOwn).slice(0, 10)
    return existingTagsForOrg
      .filter((t) => t.toLowerCase().includes(input) && excludeOwn(t))
      .slice(0, 10)
  }, [tagInput, existingTagsForOrg, contactTagSet])

  const canAddNotesTagsActivities = contactId != null

  const handleAddNote = async () => {
    if (!orgId || !contactId || !user?.id || !noteEditor) return
    const html = noteEditor.getHTML()
    if (!noteEditor.getText().trim()) return
    setSavingNote(true)
    try {
      const { error } = await supabase.from("donor_notes").insert({
        contact_id: contactId,
        org_id: orgId,
        note: html,
        created_by: user.id,
      })
      if (error) throw error
      toast.success("Note added")
      noteEditor.commands.clearContent()
      fetchContactData()
    } catch (e) {
      toast.error(errorMessage(e, "Failed to add note"))
    } finally {
      setSavingNote(false)
    }
  }

  const handleUpdateNote = async () => {
    if (!editingNoteId || !editingNoteText.trim()) return
    const note = notes.find((n) => n.id === editingNoteId)
    if (!note || note.created_by !== user?.id) return
    setSavingNote(true)
    try {
      const { error } = await supabase
        .from("donor_notes")
        .update({ note: editingNoteText.trim() })
        .eq("id", editingNoteId)
      if (error) throw error
      toast.success("Note updated")
      setEditingNoteId(null)
      setEditingNoteText("")
      fetchContactData()
    } catch (e) {
      toast.error(errorMessage(e, "Failed to update note"))
    } finally {
      setSavingNote(false)
    }
  }

  const handleDeleteNote = async () => {
    if (!deleteNoteDialog) return
    const note = notes.find((n) => n.id === deleteNoteDialog.id)
    if (!note) return
    if (!isOrgOwner) return // RLS: only org owner can delete
    setSavingNote(true)
    try {
      const { error } = await supabase.from("donor_notes").delete().eq("id", deleteNoteDialog.id)
      if (error) throw error
      toast.success("Note deleted")
      setDeleteNoteDialog(null)
      fetchContactData()
    } catch (e) {
      toast.error(errorMessage(e, "Failed to delete note"))
    } finally {
      setSavingNote(false)
    }
  }

  const canEditNote = (note: DonorNote) => note.created_by === user?.id
  const canDeleteNote = (_note: DonorNote) => isOrgOwner // RLS: only org owner can delete

  const handleAddTag = async (tag: string) => {
    const t = tag.trim()
    if (!t || !orgId || !contactId) return
    const existing = tags.some((x) => x.tag.toLowerCase() === t.toLowerCase())
    if (existing) {
      toast.error("Tag already added")
      return
    }
    setAddingTag(true)
    try {
      const { error } = await supabase.from("donor_tags").insert({
        contact_id: contactId,
        org_id: orgId,
        tag: t,
      })
      if (error) throw error
      toast.success("Tag added")
      setTagInput("")
      setTagSuggestionsOpen(false)
      fetchContactData()
    } catch (e) {
      toast.error(errorMessage(e, "Failed to add tag"))
    } finally {
      setAddingTag(false)
    }
  }

  const handleRemoveTag = async (tagRow: DonorTag) => {
    if (AUTO_TAGS.includes(tagRow.tag as (typeof AUTO_TAGS)[number])) return
    try {
      const { error } = await supabase.from("donor_tags").delete().eq("id", tagRow.id)
      if (error) throw error
      toast.success("Tag removed")
      fetchContactData()
    } catch (e) {
      toast.error(errorMessage(e, "Failed to remove tag"))
    }
  }

  const openAddActivityDialog = () => {
    setEditingActivityId(null)
    setActivityForm({
      activity_type: "event",
      activity_date: new Date().toISOString().slice(0, 10),
      description: "",
      hours: "",
    })
    setActivityDialogOpen(true)
  }

  const openEditActivityDialog = (a: VolunteerActivity) => {
    setEditingActivityId(a.id)
    setActivityForm({
      activity_type: (a.activity_type === "shift" || a.activity_type === "project" ? a.activity_type : "event") as "event" | "shift" | "project",
      activity_date: a.activity_date,
      description: a.description ?? "",
      hours: a.hours != null ? String(a.hours) : "",
    })
    setActivityDialogOpen(true)
  }

  const handleSaveActivity = async () => {
    if (!orgId || !contactId || !user?.id) return
    const hoursNum = activityForm.hours ? parseFloat(activityForm.hours) : null
    if (activityForm.hours && (isNaN(hoursNum!) || hoursNum! < 0)) {
      toast.error("Hours must be a valid number")
      return
    }
    setSavingActivity(true)
    try {
      if (editingActivityId) {
        const activity = activities.find((a) => a.id === editingActivityId)
        if (!activity || activity.created_by !== user?.id) {
          toast.error("You can only edit your own activities")
          return
        }
        const { error } = await supabase
          .from("volunteer_activities")
          .update({
            activity_type: activityForm.activity_type,
            activity_date: activityForm.activity_date,
            description: activityForm.description.trim() || null,
            hours: hoursNum,
          })
          .eq("id", editingActivityId)
        if (error) throw error
        toast.success("Activity updated")
      } else {
        const { error } = await supabase.from("volunteer_activities").insert({
          contact_id: contactId,
          org_id: orgId,
          activity_type: activityForm.activity_type,
          activity_date: activityForm.activity_date,
          description: activityForm.description.trim() || null,
          hours: hoursNum,
          created_by: user.id,
        })
        if (error) throw error
        const volunteerTag = tags.find(
          (t) => t.tag.toLowerCase() === "volunteer"
        )
        if (!volunteerTag) {
          await supabase.from("donor_tags").insert({
            contact_id: contactId,
            org_id: orgId,
            tag: "Volunteer",
          })
        }
        toast.success("Activity added")
      }
      setActivityDialogOpen(false)
      fetchContactData()
    } catch (e) {
      toast.error(errorMessage(e, "Failed to save activity"))
    } finally {
      setSavingActivity(false)
    }
  }

  const handleDeleteActivity = async () => {
    if (!deleteActivityDialog) return
    const activity = activities.find((a) => a.id === deleteActivityDialog.id)
    if (!activity) return
    if (!isOrgOwner) return // RLS: only org owner can delete
    try {
      const { error } = await supabase
        .from("volunteer_activities")
        .delete()
        .eq("id", deleteActivityDialog.id)
      if (error) throw error
      toast.success("Activity deleted")
      setDeleteActivityDialog(null)
      fetchContactData()
    } catch (e) {
      toast.error(errorMessage(e, "Failed to delete activity"))
    }
  }

  const canEditActivity = (a: VolunteerActivity) => a.created_by === user?.id
  const canDeleteActivity = () => isOrgOwner // RLS: only org owner can delete

  const handleSaveOfflineDonation = async () => {
    if (!orgId || !contactId || !contact) return
    const cents = Math.round(parseFloat(offlineForm.amount) * 100)
    if (isNaN(cents) || cents <= 0) {
      toast.error("Enter a valid amount")
      return
    }
    setSavingOffline(true)
    try {
      const ref =
        offlineForm.method === "check"
          ? `check:${offlineForm.checkNumber.trim()}`
          : "cash"
      const { error } = await supabase.from("donations").insert({
        org_id: orgId,
        contact_id: contactId,
        donor_name: contact.name ?? contact.email,
        donor_email: contact.email,
        amount: cents,
        currency: "usd",
        status: "succeeded",
        stripe_charge_id: ref,
        created_at: new Date(offlineForm.date).toISOString(),
      })
      if (error) throw error
      toast.success("Donation recorded")
      setOfflineDonationOpen(false)
      setOfflineForm({ amount: "", date: new Date().toISOString().slice(0, 10), method: "cash", checkNumber: "" })
      fetchContactData()
    } catch (e) {
      toast.error(errorMessage(e, "Failed to record donation"))
    } finally {
      setSavingOffline(false)
    }
  }

  const getStripeLink = (donation: Donation) => {
    const base = import.meta.env.VITE_STRIPE_MODE === "live" ? "" : "test/"
    return `https://dashboard.stripe.com/${base}payments/${donation.stripe_payment_intent_id}`
  }

  if (loading && !contact) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background">
        <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden />
        <p className="text-muted-foreground">Loading contact...</p>
      </div>
    )
  }

  if (!contact) {
    return (
      <div className="min-h-screen bg-background p-4 sm:p-6">
        <p className="text-destructive">Contact not found.</p>
        <Button variant="outline" size="sm" asChild className="mt-4">
          <Link to="/dashboard/contacts">Back to Contacts</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="bg-background">
      <div className="p-4 sm:p-6">
        {/* Header: Back, contact name, email, tags */}
        <div className="mb-6">
          <Button variant="ghost" size="sm" asChild className="-ml-2 mb-4">
            <Link to="/dashboard/contacts" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Contacts
            </Link>
          </Button>
          <h2 className="text-2xl font-bold">{contact.name || contact.email || "Unknown"}</h2>
          <a
            href={`mailto:${contact.email}`}
            className="mt-1 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <Mail className="h-4 w-4" />
            {contact.email}
          </a>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {tags.map((t) => (
              <Badge
                key={t.id}
                variant={AUTO_TAGS.includes(t.tag as (typeof AUTO_TAGS)[number]) ? "default" : "secondary"}
                className="cursor-default gap-1"
                title={AUTO_TAGS.includes(t.tag as (typeof AUTO_TAGS)[number]) ? "This tag is automatically managed" : undefined}
              >
                {t.tag}
                {!AUTO_TAGS.includes(t.tag as (typeof AUTO_TAGS)[number]) && (
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(t)}
                    className="ml-1 rounded-full hover:bg-muted"
                    aria-label={`Remove ${t.tag}`}
                  >
                    ×
                  </button>
                )}
              </Badge>
            ))}
            {canAddNotesTagsActivities && (
              <div className="relative">
                <div className="flex gap-2">
                  <Input
                    ref={tagInputRef}
                    placeholder="Add tag..."
                    value={tagInput}
                    onChange={(e) => {
                      setTagInput(e.target.value)
                      setTagSuggestionsOpen(true)
                    }}
                    onFocus={() => setTagSuggestionsOpen(true)}
                    onBlur={() => {
                      setTimeout(() => setTagSuggestionsOpen(false), 150)
                    }}
                    className="w-40"
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      if (tagInput.trim()) handleAddTag(tagInput.trim())
                      else if (tagSuggestions[0]) handleAddTag(tagSuggestions[0])
                    }}
                    disabled={addingTag || (!tagInput.trim() && tagSuggestions.length === 0)}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Add
                  </Button>
                </div>
                {tagSuggestionsOpen && (tagSuggestions.length > 0 || tagInput.trim()) && (
                  <div
                    ref={tagSuggestionsRef}
                    className="absolute left-0 top-full z-10 mt-1 max-h-48 w-48 overflow-auto rounded-md border bg-popover p-1 shadow-md"
                  >
                    {tagInput.trim() && !contactTagSet.has(tagInput.trim().toLowerCase()) && (
                      <button
                        type="button"
                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                        onClick={() => handleAddTag(tagInput.trim())}
                      >
                        <Plus className="h-4 w-4" />
                        Create "{tagInput.trim()}"
                      </button>
                    )}
                    {tagSuggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className="flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                        onClick={() => handleAddTag(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Summary cards */}
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Donated
              </CardTitle>
            </CardHeader>
            <CardContent>
              {canViewFinancials ? (
                <p className="text-2xl font-bold">{formatCurrency(totalDonated, { digits: 0 })}</p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  You don&apos;t have permission to view financial data
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Volunteer Hours
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{volunteerHours} hours</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Last Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {formatRelativeTime(lastActivityDate)}
              </p>
            </CardContent>
          </Card>
        </div>

        {canManageContent && currentOrg && orgId && (
          <>
            <GenerateFundraiserLinkModal
              open={fundraiserLinkModalOpen}
              onOpenChange={setFundraiserLinkModalOpen}
              orgId={orgId}
              org={{ slug: currentOrg.slug, custom_domain: currentOrg.custom_domain }}
              defaultName={contact.name ?? ""}
              defaultEmail={contact.email}
              existingLinksByCampaignId={Object.fromEntries(
                fundraiserLinks.map((f) => [f.campaign_id, f])
              )}
              onCreated={fetchContactData}
            />
            {/* Fundraiser links view modal */}
            <Dialog open={fundraiserLinksViewOpen} onOpenChange={setFundraiserLinksViewOpen}>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Fundraising links</DialogTitle>
                </DialogHeader>
                <div className="space-y-2">
                  {fundraiserLinks.map((f) => {
                    const url = `${getPublicSiteOrigin({ slug: currentOrg.slug, custom_domain: currentOrg.custom_domain })}/campaign/${f.campaigns?.slug}/fundraise/${f.share_token}`
                    return (
                      <div key={f.id} className="space-y-1 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium">{f.campaigns?.title ?? "Campaign"}</p>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="shrink-0"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(url)
                                toast.success("Copied to clipboard")
                              } catch {
                                toast.error("Could not copy")
                              }
                            }}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="break-all font-mono text-xs text-muted-foreground">{url}</p>
                      </div>
                    )
                  })}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setFundraiserLinksViewOpen(false)}>Close</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="mb-3 flex items-center justify-between gap-2">
            {/* Left: fundraiser buttons — always visible */}
            <div className="flex gap-2">
              {canManageContent && currentOrg && orgId && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setFundraiserLinkModalOpen(true)}>
                    Generate fundraising link
                  </Button>
                  {fundraiserLinks.length > 0 && (
                    <Button size="sm" variant="ghost" onClick={() => setFundraiserLinksViewOpen(true)}>
                      View links ({fundraiserLinks.length})
                    </Button>
                  )}
                </>
              )}
            </div>
            {/* Right: tab-specific action */}
            <div>
              {activeTab === "donations" && canViewFinancials && (
                <Button size="sm" onClick={() => setOfflineDonationOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add offline donation
                </Button>
              )}
              {activeTab === "activities" && canAddNotesTagsActivities && (
                <Button size="sm" onClick={openAddActivityDialog}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add activity
                </Button>
              )}
            </div>
          </div>
          <TabsList className="mb-4 grid w-full grid-cols-5 sm:w-auto">
            <TabsTrigger value="donations">Donations</TabsTrigger>
            <TabsTrigger value="activities">Volunteer Activities</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="submissions">Form Submissions</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
          </TabsList>

          <TabsContent value="donations">
            <div className="rounded-md border">
              {!canViewFinancials ? (
                <p className="p-8 text-center text-muted-foreground">
                  You don&apos;t have permission to view donation history.
                </p>
              ) : donations.length === 0 ? (
                <p className="p-8 text-center text-muted-foreground">
                  No donations yet.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {donations.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell>{formatDate(d.created_at)}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(d.amount, { digits: 0 })}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              d.status === "succeeded"
                                ? "default"
                                : d.status === "pending"
                                  ? "secondary"
                                  : "destructive"
                            }
                          >
                            {d.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {d.stripe_payment_intent_id
                            ? "Stripe"
                            : d.stripe_charge_id?.startsWith("check:")
                              ? `Check #${d.stripe_charge_id.slice(6)}`
                              : d.stripe_charge_id === "cash"
                                ? "Cash"
                                : "—"}
                        </TableCell>
                        <TableCell>
                          {d.stripe_payment_intent_id && (
                            <a
                              href={getStripeLink(d)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              View <ExternalLink className="ml-1 inline h-3 w-3" />
                            </a>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="activities">
            <div className="space-y-4">
              <div className="rounded-md border">
                {activities.length === 0 ? (
                  <p className="p-8 text-center text-muted-foreground">
                    No volunteer activities yet.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activities.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell>{formatDate(a.activity_date)}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              {formatActivityType(a.activity_type)}
                            </Badge>
                          </TableCell>
                          <TableCell>{a.description || "—"}</TableCell>
                          <TableCell className="text-right">
                            {a.hours != null ? a.hours : "—"}
                          </TableCell>
                          <TableCell>
                            {canEditActivity(a) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditActivityDialog(a)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            {canDeleteActivity() && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteActivityDialog({ id: a.id })}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="events">
            <div className="rounded-md border">
              {!canManageContacts ? (
                <div className="p-6 text-center text-muted-foreground">
                  You don&apos;t have permission to view events.
                </div>
              ) : eventRegistrations.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  No event registrations yet.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Guests</TableHead>
                      <TableHead>Registered</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {eventRegistrations.map((reg) => {
                      const ev = reg.events
                      return (
                        <TableRow key={reg.id}>
                          <TableCell>
                            {ev ? (
                              <Link
                                to={`/dashboard/events/${ev.id}/edit`}
                                className="font-medium hover:underline"
                              >
                                {ev.title}
                              </Link>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell>{ev ? formatDate(ev.start_date) : "—"}</TableCell>
                          <TableCell>{reg.guest_count > 0 ? reg.guest_count : "—"}</TableCell>
                          <TableCell>{formatRelativeTime(reg.created_at)}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="submissions">
            <div className="rounded-md border">
              {!canManageContacts ? (
                <p className="p-8 text-center text-muted-foreground">
                  You don&apos;t have permission to view form submissions.
                </p>
              ) : formSubmissions.length === 0 ? (
                <p className="p-8 text-center text-muted-foreground">No form submissions yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Form</TableHead>
                      <TableHead>Fields</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formSubmissions.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="whitespace-nowrap">{formatDate(s.created_at)}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {s.page_title ?? "—"}
                        </TableCell>
                        <TableCell className="max-w-sm">
                          <span className="text-sm text-muted-foreground">
                            {Object.entries(s.form_data)
                              .map(([k, v]) => `${k}: ${String(v)}`)
                              .join(" · ")}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              s.status === "new"
                                ? "default"
                                : s.status === "archived"
                                  ? "outline"
                                  : "secondary"
                            }
                          >
                            {s.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="notes">
            <div className="space-y-4">
              {canAddNotesTagsActivities && (
                <div className="space-y-2">
                  <div className="overflow-hidden rounded-md border focus-within:ring-1 focus-within:ring-ring">
                    <div className="flex gap-0.5 border-b bg-muted/50 px-2 py-1">
                      <Button
                        type="button"
                        size="icon"
                        variant={noteEditor?.isActive("bold") ? "secondary" : "ghost"}
                        className="h-7 w-7"
                        onMouseDown={(e) => {
                          e.preventDefault()
                          noteEditor?.chain().focus().toggleBold().run()
                        }}
                      >
                        <Bold className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant={noteEditor?.isActive("italic") ? "secondary" : "ghost"}
                        className="h-7 w-7"
                        onMouseDown={(e) => {
                          e.preventDefault()
                          noteEditor?.chain().focus().toggleItalic().run()
                        }}
                      >
                        <Italic className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant={noteEditor?.isActive("bulletList") ? "secondary" : "ghost"}
                        className="h-7 w-7"
                        onMouseDown={(e) => {
                          e.preventDefault()
                          noteEditor?.chain().focus().toggleBulletList().run()
                        }}
                      >
                        <List className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <div className="bg-background px-3 py-2">
                      <EditorContent editor={noteEditor} />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={handleAddNote} disabled={savingNote}>
                      {savingNote ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Save note
                    </Button>
                  </div>
                </div>
              )}
              <div className="space-y-4">
                {notes.length === 0 ? (
                  <p className="text-muted-foreground">No notes yet.</p>
                ) : (
                  notes.map((note) => (
                    <Card key={note.id}>
                      <CardContent className="pt-4">
                        {editingNoteId === note.id ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editingNoteText}
                              onChange={(e) => setEditingNoteText(e.target.value)}
                              rows={3}
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={handleUpdateNote}
                                disabled={savingNote || !editingNoteText.trim()}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingNoteId(null)
                                  setEditingNoteText("")
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {note.note.startsWith("<") ? (
                              <div
                                className="prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: sanitizeHtml(note.note) }}
                              />
                            ) : (
                              <p className="whitespace-pre-wrap">{note.note}</p>
                            )}
                            <p className="mt-2 text-xs text-muted-foreground">
                              — {formatRelativeTime(note.created_at)}
                            </p>
                            {(canEditNote(note) || canDeleteNote(note)) && (
                              <div className="mt-2 flex gap-2">
                                {canEditNote(note) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setEditingNoteId(note.id)
                                      setEditingNoteText(note.note)
                                    }}
                                  >
                                    <Pencil className="mr-1 h-3 w-3" />
                                    Edit
                                  </Button>
                                )}
                                {canDeleteNote(note) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDeleteNoteDialog({ id: note.id })}
                                  >
                                    <Trash2 className="mr-1 h-3 w-3 text-destructive" />
                                    Delete
                                  </Button>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Offline Donation Dialog */}
      <Dialog open={offlineDonationOpen} onOpenChange={setOfflineDonationOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add offline donation</DialogTitle>
            <DialogDescription>Record a cash or check donation received outside of the platform.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="od-amount">Amount (USD)</Label>
              <Input
                id="od-amount"
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.00"
                value={offlineForm.amount}
                onChange={(e) => setOfflineForm((f) => ({ ...f, amount: e.target.value }))}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="od-date">Date received</Label>
              <Input
                id="od-date"
                type="date"
                value={offlineForm.date}
                onChange={(e) => setOfflineForm((f) => ({ ...f, date: e.target.value }))}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="od-method">Payment method</Label>
              <Select
                value={offlineForm.method}
                onValueChange={(v) => setOfflineForm((f) => ({ ...f, method: v as "cash" | "check" }))}
              >
                <SelectTrigger id="od-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {offlineForm.method === "check" && (
              <div className="grid gap-1.5">
                <Label htmlFor="od-check">Check number</Label>
                <Input
                  id="od-check"
                  placeholder="e.g. 1042"
                  value={offlineForm.checkNumber}
                  onChange={(e) => setOfflineForm((f) => ({ ...f, checkNumber: e.target.value }))}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOfflineDonationOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveOfflineDonation} disabled={savingOffline || !offlineForm.amount}>
              {savingOffline ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save donation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Activity Dialog */}
      <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingActivityId ? "Edit Activity" : "Add Volunteer Activity"}
            </DialogTitle>
            <DialogDescription>
              Record a volunteer activity for this contact.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Activity Type</Label>
              <Select
                value={activityForm.activity_type}
                onValueChange={(v) =>
                  setActivityForm((f) => ({ ...f, activity_type: v as "event" | "shift" | "project" }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="event">Event</SelectItem>
                  <SelectItem value="shift">Shift</SelectItem>
                  <SelectItem value="project">Project</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={activityForm.activity_date}
                onChange={(e) =>
                  setActivityForm((f) => ({ ...f, activity_date: e.target.value }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={activityForm.description}
                onChange={(e) =>
                  setActivityForm((f) => ({ ...f, description: e.target.value }))
                }
                placeholder="Describe the activity..."
                rows={3}
              />
            </div>
            <div className="grid gap-2">
              <Label>Hours (optional)</Label>
              <Input
                type="number"
                min="0"
                step="0.5"
                value={activityForm.hours}
                onChange={(e) =>
                  setActivityForm((f) => ({ ...f, hours: e.target.value }))
                }
                placeholder="e.g. 2.5"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivityDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveActivity} disabled={savingActivity}>
              {savingActivity ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {editingActivityId ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Note Confirm */}
      <Dialog
        open={!!deleteNoteDialog}
        onOpenChange={(open) => !open && setDeleteNoteDialog(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this note?</DialogTitle>
            <DialogDescription>
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteNoteDialog(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteNote} disabled={savingNote}>
              {savingNote ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Activity Confirm */}
      <Dialog
        open={!!deleteActivityDialog}
        onOpenChange={(open) => !open && setDeleteActivityDialog(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this activity?</DialogTitle>
            <DialogDescription>
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteActivityDialog(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteActivity}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
